// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import { setupNativeBridge } from './WKBridge';

/**
 * Names of all callbacks from app to web
 */
type AppToWebBridgeAction =
  | 'updateHeaderHeight'
  | 'shrinkMap'
  | 'navigateToTripDetail'
  | 'refetchTrips'
  | 'refetchTripsWithItems';

/**
 * Incoming context from native for updating the header height
 */
export type UpdateHeaderHeightContext = {|
  /** Height of the header in points */
  headerHeight?: ?number,
|};

/**
 * Incoming context from native for navigating from trips to trip detail
 */
export type NavigateToTripDetailContext = {|
  /** id of trip */
  tripId?: ?number,
  /** id of trip item (used for linking directly to a trip item) */
  itemId?: ?number,
|};

/**
 * Incoming context from native for handling trip collaboration links
 */
export type HandleTripCollaborationContext = {|
  /** collaboration key */
  tt?: ?string,
|};

/**
 * JSON definition for an individual item in a natively rendered overflow menu
 * Callers define a callback function using tapCallback, which internally is translated
 * into a tapFunction string. The use case for nestedMenu is for when a submenu pops up on
 * tap of this item.
 */
export type NativeOverflowMenuItem = {|
  /** Optional string defining the icon to use */
  icon?: string,
  /** Optional string defining the title. Either icon or title is required */
  title?: string,
  /** selected by default */
  preferred?: boolean,
  /** Optional boolean saying if the item should be rendered as destructive (red) */
  destructive?: ?boolean,
  /** For internal use only */
  tapFunction?: string,
  /** Callback function when the user taps on the item */
  tapCallback?: ?Function,
  /** Nested menu that appears on tap */
  // eslint-disable-next-line no-use-before-define
  nestedMenu?: ?NativeOverflowMenu,
  /** Android back button */
  handleAsHardBackAction?: boolean,
|};

/**
 * JSON definition for a natively rendered overflow menu.
 * The only supported treatment at present is "bottom", but this may be expanded to additional display
 * types in the future.
 */
export type NativeOverflowMenu = {|
  /** Optional title to display above the menu items */
  title?: string,
  /** sub title */
  subtitle?: string,
  /** Display treatment to use. Currently only supports bottom */
  treatment: string,
  /** Items appearing in the menu */
  items: NativeOverflowMenuItem[],
|};

/**
 * JSON definition for data to send for a saves object
 */
export type NativeSavesObject = {|
  /** The id of the saves wrapper object */
  id: number | string,
  /** The type of the saves object */
  type: string,
  /** The main attached note for this object. Used when resaving. */
  note: ?string,
  /** The underlying object saved */
  originalObject: ?{|
    /** The id of the original object */
    id: number | string,
    /** The type of the original object */
    type: string,
  |},
|};

/**
 * JSON definition for the subset of a review summary for a map pin on native
 */
type ReviewSummary = {|
  count: number,
  rating: number,
|};

type AdditionalNames = {|
  long?: string,
  longOnlyParent?: string,
  longParentAbbreviated?: string,
|};

type PhotoSizes = {|
  width: number,
  height: number,
  url: string,
|};

type ThumbnailPhoto = {|
  id: number,
  photoSizes?: PhotoSizes,
|};

type Trip = {|
  /** id of the trip */
  id: number,
  /** title of the trip */
  title: ?string,
  /** description of the trip */
  description?: string,
|};

type TripItem = {|
  /** id of the trip item */
  id: number,
  /** id of the trip */
  tripId: number,
  /** name of the item */
  name: ?string,
  /** id of the location */
  locationId: any,
  /** location, attractionProduct, or savesItem */
  saveType: any,
|};

type Comment = {|
  commentId: number,
  avatarUrl?: string,
  authorUsername: string,
  body: string,
|};

/**
 * JSON definition for a location information on native.
 */
export type LocationInformation = {|
  /** Latitude of the point */
  latitude: number,
  /** Longitude of the point */
  longitude: number,
  /** Name of the location */
  name: string,
  /** TripAdvisor ID of the location */
  locationId: string,
  /** Place type string */
  placeType: string,
  /** Accomodation category of the place type */
  accommodationCategory?: string,
  /** Review summary information of the location, used for bubbles */
  reviewSummary?: ReviewSummary,
  /** Strings for the name of the POI */
  additionalNames?: AdditionalNames,
  /** Info of the parent */
  parent?: $Shape<LocationInformation>,
  /** Thumbnail of the location information */
  thumbnail?: ThumbnailPhoto,
  /** First comment */
  topComment: ?Comment,
|};

/** An map pin is just an alias for location information */
export type NativeMapPin = LocationInformation;

/**
 * JSON definition for how to render the top navigation bar
 */
export type NativeNavigationContext = {|
  /** Title to show in the navigation bar */
  title: string,
  /** True if the back button should be hidden */
  hideBackButton?: ?boolean,
  /** Set to a positive number to hide the navigation bar
   * when the user is scrolled above this scroll height in points
   */
  navbarHiddenAboveScrollHeight?: ?number,
  /** Menu toolbar items appearing next to the navbar title */
  trailingMenuItems?: ?(NativeOverflowMenuItem[]),
|};

/**
 * JSON definition for how to render the native map on a given screen
 */
export type NativeMapContext = {|
  /** List of map pins */
  mapPins?: NativeMapPin[],
  /** Zoom level to use */
  zoom?: ?number,
  /** Center location */
  center?: ?any,
|};

/**
 * Top-level JSON definition about how to render a new native screen
 */
export type NativeScreenContext = {|
  /** How to render the native navigation bar */
  navigation: NativeNavigationContext,
  /** How to render the native map */
  map?: ?NativeMapContext,
|};

/**
 * JSON definition for sharing an item using the share sheet
 */
export type NativeShareTripContext = {|
  /** Trip to share */
  trip: Trip,
  /** Item that is being shared, if applicable */
  item: ?TripItem,
|};

/* eslint-disable */
function adjustNativeOverflowMenuItem(item: NativeOverflowMenuItem) {
  let tapFunction: string = '';
  if (item.tapCallback != null) {
    let originalCallback = item.tapCallback;
    const callbackName = 'nativeInterop' + guid();
    window[callbackName] = () => {
      if (originalCallback != null) {
        originalCallback();
      }
    };
    tapFunction = callbackName;
    item.tapFunction = tapFunction;
    item['tapCallback'] = null;
  }
}

/**
 * Get the bridge uid sent from the app. Initializes native bridge if not yet initialized
 * @returns uid or null if it does not apply
 */
function bridgeUid(): ?string {
  if (typeof window === 'undefined' || !window.taBridgeContext) {
    return null;
  }
  if (!window.bridge) {
    setupNativeBridge();
  }
  return window.taBridgeContext.uid;
}

export function isActionSupported(action: string, version: number): boolean {
  if (typeof window === 'undefined' || !window.taBridgeContext || !window.taBridgeContext.actions) {
    return false;
  }
  const actionVersion = window.taBridgeContext.actions[action];
  if (!actionVersion || !actionVersion.v) {
    return false;
  }
  return version === actionVersion.v;
}

function toggleAppCallback(enable: boolean, action: AppToWebBridgeAction, callback: () => mixed) {
  if (!bridgeUid()) {
    return;
  }
  if (enable) {
    window.bridge.on(action, callback);
  } else {
    window.bridge.off(action);
  }
}
/* eslint-enable */

function adjustNativeOverflowMenu(menu: NativeOverflowMenu) {
  // eslint-disable-next-line
  menu.items.forEach(function(key: NativeOverflowMenuItem, value: number) {
    const item: NativeOverflowMenuItem = key;
    adjustNativeOverflowMenuItem(item);
  });
}

function adjustNativeNavigationContext(context: NativeNavigationContext) {
  if (context.trailingMenuItems != null) {
    // eslint-disable-next-line
    context.trailingMenuItems.forEach(function(key: NativeOverflowMenuItem, value: number) {
      const item: NativeOverflowMenuItem = key;
      adjustNativeOverflowMenuItem(item);
      if (item.nestedMenu != null) {
        adjustNativeOverflowMenu(item.nestedMenu);
      }
    });
  }
}

function guid(): string {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return s4() + s4() + s4() + s4() + s4() + s4() + s4() + s4();
}

/**
 * Post an action to the native bridge
 * @param action Name of action
 * @param version Version of action
 * @param object Object to send down with action
 * @return if the action was sent
 */
export function postToBridge(action: string, version: number, object: any): boolean {
  const uid = bridgeUid();
  if (!uid || !isActionSupported(action, version)) {
    return false;
  }
  // eslint-disable-next-line
  window.bridge.post(action, object || {}, uid);
  return true;
}

/**
 * Hides the skeleton view on native
 */
export function hideSkeleton() {
  postToBridge('hideSkeleton', 1.0, null);
}

/**
 * Invoke this when the user visits a new screen within a single page webapp.
 * Is idempotent once the user is on the screen.
 * It is ok to call multiple times once on the new screen, e.g. to update partial data
 * on an existing screen such as new map pins.
 * @param context Includes context for the native app navigation bar plus any other data needed for the new screen.
 */
export function onNewOrUpdatedScreen(context: NativeScreenContext) {
  adjustNativeNavigationContext(context.navigation);
  postToBridge('onNewOrUpdatedScreen', 1.0, context);
}

/**
 * Pop a native styled menu (UIAlertController on iOS)
 * @param menu Details about the menu, and javascript callback functions for each menu item.
 */
export function showNativeOverflowMenu(menu: NativeOverflowMenu) {
  adjustNativeOverflowMenu(menu);
  postToBridge('overflowMenu', 1.0, menu);
}

/**
 * Show the native map screen
 */
export function showNativeMap() {
  if (isActionSupported('map', 2.0)) {
    postToBridge('map', 2.0, null);
  } else {
    postToBridge('map', 1.0, null);
  }
}

/**
 * Show the native create a trip screen
 * @param If the user has selected public trips. Informs the app to automatically set a new trip to public.
 */
export function showNativeCreateTrip(isPublic: boolean) {
  postToBridge('createTrip', 1.0, { isPublic });
}

/**
 * Show the native share action sheet
 * @param context Native
 */

export function showNativeShareTrip(context: NativeShareTripContext) {
  postToBridge('shareTrip', 1.0, context);
}

/**
 * Prompts native to present the native STAT modal
 */
export const showSavesModal = (savesObject: NativeSavesObject) => {
  const actionName = 'presentSavesModal';
  if (isActionSupported(actionName, 2.0)) {
    postToBridge(actionName, 2.0, savesObject);
  } else {
    postToBridge(actionName, 1.0, { itemId: savesObject.id });
  }
};

/**
 * Prompts native to show the native registration form
 * @dismissOnCancellation true if the native app should dismiss whatever screen presented it if registration is cancelled
 * This allows views to be 'gated' by login
 */
export const showRegistration = (dismissOnCancellation: boolean) => {
  postToBridge('showReg', 1.0, { dismissOnCancellation });
};

/**
 * Close the native map screen
 */
export function close() {
  postToBridge('close', 1.0, null);
}

/**
 * Start a native screen transition. Triggers a native animation sequence
 * using screenshotting.
 * Web components are responsible for actually transitioning to the next
 * screen after making this call.
 */
export function startScreenTransition() {
  postToBridge('startScreenTransition', 1.0, null);
}

/**
 * Toggle an overlay
 */
export function onOverlayStateChange(shown: boolean) {
  postToBridge(shown ? 'overlayShown' : 'overlayHidden', 1.0, null);
}

/**
 * Toggle on or off the native callback to change the header height
 * @param enable Whether the callback should be invoked
 * @param callback The callback containing the height
 */
export function onHeaderHeightChange(
  enable: boolean,
  callback: UpdateHeaderHeightContext => mixed,
) {
  // $FlowFixMe
  toggleAppCallback(enable, 'updateHeaderHeight', callback);
}

/**
 * Toggle on or off the native callback to shrink the map
 * @param enable Whether the callback should be invoked
 * @param callback The callback
 */
export function onShrinkMap(enable: boolean, callback: () => mixed) {
  toggleAppCallback(enable, 'shrinkMap', callback);
}

/**
 * Toggle on or off the native callback for navigating to trip detail
 * @param enable Whether the callback should be invoked
 * @param callback The callback containing the trip id and item id
 */
export function onNavigateToTripDetail(
  enable: boolean,
  callback: NavigateToTripDetailContext => mixed,
) {
  // $FlowFixMe
  toggleAppCallback(enable, 'navigateToTripDetail', callback);
}

/**
 * Toggle on or off the native callback for navigating to trip detail
 * @param enable Whether the callback should be invoked
 * @param callback The callback containing the trip id and item id
 */
export function onHandleTripCollaboration(
  enable: boolean,
  callback: HandleTripCollaborationContext => mixed,
) {
  // $FlowFixMe
  toggleAppCallback(enable, 'handleTripCollaboration', callback);
}

/**
 * True if this is a native webview
 * @returns {boolean}
 */
export function isNativeWebview(): boolean {
  return bridgeUid() != null;
}

/**
 * Get navigation bar height
 * @returns {*}
 */
export function getHeaderHeight(): number {
  /* eslint-disable no-restricted-globals */
  if (
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined' &&
    typeof window.localStorage.headerHeight !== 'undefined'
  ) {
    let { headerHeight } = window.localStorage;
    if (typeof headerHeight === 'string') {
      headerHeight = parseInt(headerHeight, 10);
    }

    return headerHeight;
  }
  return 0;
}

/**
 * Callback for whenever the app updates a trips context (i.e. adding items to a trip, creating a trip)
 * This should NOT be called on trip deletes/item deletes, as those are already handled on web.
 *
 *
 * @param enable whether or not the callback should be invoked
 * @param callback The callback
 */
export function onTripsRefetch(enable: boolean, callback: () => void): void {
  toggleAppCallback(enable, 'refetchTrips', callback);
}

/**
 * Update trip title when title is changed, so native can show the correct toast
 * @param trip The trip that is being updated
 */
export function onTripUpdate(trip: Trip) {
  postToBridge('tripUpdate', 1.0, trip);
}

/**
 * Inform native when an item was deleted from the trip
 * @param item The item that was deleted
 */
export function onTripItemDelete(item: TripItem) {
  postToBridge('tripItemDelete', 1.0, item);
}

/**
 * Callback for whenever there's a modification in any of trip's items using the STAT flow in the app.
 *
 *
 * @param enable whether or not the callback should be invoked
 * @param callback The callback
 */
export function onRefetchTripsWithItems(enable: boolean, callback: () => void): void {
  toggleAppCallback(enable, 'refetchTripsWithItems', callback);
}
